'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useLocale } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';
import { toast } from 'sonner';

import { DownloadManagerPanel } from '@/components/download-manager/download-manager-panel';
import { useAppHaptics } from '@/hooks/use-app-haptics';
import { getDownloadManagerCopy } from '@/i18n/download-manager-copy';
import { registerManagedAssetDownloadHandler } from '@/lib/managed-asset-download';
import { useDownloadManagerBackend } from '@/lib/download-manager-backend';
import { createAutoDeliveryCoordinator } from '@/lib/download-manager-delivery';
import {
  createLocalDownloadRunner,
  type LocalDownloadRun,
} from '@/lib/download-manager-local-runner';
import {
  addLocalDownloadFailure,
  closedLocalDownloadTokens,
  createLocalDownloadHighWater,
  isClosedLocalDownloadReport,
  addLocalDownloadPreparing,
  createLocalRetryGate,
  localDownloadJobId,
  isLocalDownloadJob,
  removeLocalDownloadFailure,
  type LocalDownloadFailure,
  type LocalDownloadFailureReport,
} from '@/lib/download-manager-local-failures';
import { deliverManagedDownload } from '@/lib/managed-download-delivery';
import {
  DOWNLOAD_MANAGER_DOCK_STORAGE_KEY,
  getPersistedDownloadManagerDockCorner,
  type DownloadManagerDockCorner,
} from '@/lib/download-manager-docking';
import {
  canCloseDownloadManager,
  createDownloadManagerPresentationState,
  getDownloadManagerCollapsedJob,
  reduceDownloadManagerPresentation,
  shouldRenderDownloadManager,
  isEmptyFolderDownloadJob,
  type DownloadManagerJob,
} from '@/lib/download-manager-state';

type DownloadManagerContextValue = {
  handleFolderDownloadStart: (result: FolderDownloadStartResult) => void;
  registerAutoDelivery: (jobId: string) => void;
  /** A download that failed in this browser: a failed row, never a toast. */
  reportDownloadFailure: (report: LocalDownloadFailureReport) => void;
  /**
   * Prepares a download in this browser (a conversion) as a panel row with
   * its timeout and Retry. The panel owns it, so the menu that started it
   * may close at once. Resolves when it settles or times out.
   */
  runLocalDownload: (job: LocalDownloadRun) => Promise<void>;
  /**
   * A new attempt's token for a file download that reports here: pass it
   * with that attempt's failure, and clear with it on success, so a newer
   * attempt clears an older failed row but never the reverse.
   */
  nextLocalDownloadToken: () => number;
  /** Removes the row for `key` if it is `token`'s attempt or older. */
  clearLocalDownload: (key: string, token: number) => void;
};

type FolderDownloadStartResult =
  | { status: 'empty' }
  | { status: 'started'; jobId: string };

const DownloadManagerContext =
  createContext<DownloadManagerContextValue | null>(null);

export function DownloadManagerProvider({ children }: { children: ReactNode }) {
  const params = useParams<{ orgSlug: string }>();
  const pathname = usePathname();
  const locale = useLocale();
  const { trigger } = useAppHaptics();
  const backend = useDownloadManagerBackend(params.orgSlug);
  const copy = useMemo(() => getDownloadManagerCopy(locale), [locale]);
  const acknowledgeDownloadDelivery = backend.acknowledgeDelivery;
  const mintDownloadUrl = backend.mintDownloadUrl;
  const startDirectAssetDownload = backend.startDirectAssetDownload;
  const suppressManagerJobs = backend.suppressManagerJobs;
  const [presentation, dispatch] = useReducer(
    reduceDownloadManagerPresentation,
    undefined,
    createDownloadManagerPresentationState
  );
  const [isDeliveryLocked, setIsDeliveryLocked] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [hasResolvedDockCorner, setHasResolvedDockCorner] = useState(false);
  const [localFailures, setLocalFailures] = useState<LocalDownloadFailure[]>(
    []
  );
  const [runLocalRetry] = useState(createLocalRetryGate);
  // Failed in-browser downloads first, then the server feed.
  const jobs = useMemo(
    () => [...localFailures.map((failure) => failure.job), ...backend.jobs],
    [backend.jobs, localFailures]
  );

  const setDeliveryLocked = useCallback((isLocked: boolean) => {
    setIsDeliveryLocked(isLocked);
  }, []);

  const consumeAutoDeliveryIntent = useCallback((jobId: string) => {
    dispatch({ type: 'consume-auto-delivery', jobId });
  }, []);

  const [coordinator] = useState(() =>
    createAutoDeliveryCoordinator({
      deliver: async (job) => {
        setDownloadingJobId(job.id);
        try {
          await deliverJob(job, mintDownloadUrl);
          trigger('success');
        } finally {
          setDownloadingJobId(null);
        }
      },
      acknowledge: acknowledgeDownloadDelivery,
      onDeliveryStateChange: setDeliveryLocked,
      onIntentConsumed: consumeAutoDeliveryIntent,
      onTerminalWithoutDelivery: async (job) => {
        if (!isEmptyFolderDownloadJob(job)) return;
        toast.info(copy.noFilesInFolder);
        await suppressManagerJobs([job.id]);
      },
    })
  );

  useEffect(() => {
    coordinator.updateOptions({
      deliver: async (job) => {
        setDownloadingJobId(job.id);
        try {
          await deliverJob(job, mintDownloadUrl);
          trigger('success');
        } finally {
          setDownloadingJobId(null);
        }
      },
      acknowledge: acknowledgeDownloadDelivery,
      onDeliveryStateChange: setDeliveryLocked,
      onIntentConsumed: consumeAutoDeliveryIntent,
      onTerminalWithoutDelivery: async (job) => {
        if (!isEmptyFolderDownloadJob(job)) return;
        toast.info(copy.noFilesInFolder);
        await suppressManagerJobs([job.id]);
      },
    });
  }, [
    acknowledgeDownloadDelivery,
    consumeAutoDeliveryIntent,
    copy.noFilesInFolder,
    coordinator,
    setDeliveryLocked,
    mintDownloadUrl,
    suppressManagerJobs,
    trigger,
  ]);

  useEffect(() => {
    void coordinator.updateJobs(backend.jobs);
  }, [backend.jobs, coordinator]);

  useEffect(() => {
    try {
      dispatch({
        type: 'dock',
        corner: getPersistedDownloadManagerDockCorner(
          window.localStorage.getItem(DOWNLOAD_MANAGER_DOCK_STORAGE_KEY)
        ),
      });
    } catch {
      // Keep the default corner when browser storage is unavailable.
    } finally {
      setHasResolvedDockCorner(true);
    }
  }, []);

  useEffect(() => {
    const hasActiveJob = backend.jobs.some(
      (job) => job.status === 'queued' || job.status === 'running'
    );
    if (hasActiveJob && !presentation.isOpen) {
      dispatch({ type: 'expand' });
    }
  }, [backend.jobs, presentation.isOpen]);

  const registerAutoDelivery = useCallback(
    (jobId: string) => {
      coordinator.register(jobId);
      dispatch({ type: 'register-auto-delivery', jobId });
    },
    [coordinator]
  );

  const handleFolderDownloadStart = useCallback(
    (result: FolderDownloadStartResult) => {
      if (result.status === 'empty') {
        toast.info(copy.noFilesInFolder);
        return;
      }
      registerAutoDelivery(result.jobId);
    },
    [copy.noFilesInFolder, registerAutoDelivery]
  );

  useEffect(
    () =>
      registerManagedAssetDownloadHandler(async (request) => {
        const started = await startDirectAssetDownload(request);
        registerAutoDelivery(started.jobId);
      }),
    [registerAutoDelivery, startDirectAssetDownload]
  );

  const handleDockCornerChange = useCallback(
    (corner: DownloadManagerDockCorner) => {
      dispatch({ type: 'dock', corner });
      try {
        window.localStorage.setItem(DOWNLOAD_MANAGER_DOCK_STORAGE_KEY, corner);
      } catch {
        // Docking still works for the current session without persistence.
      }
    },
    []
  );

  // Attempts whose rows the member closed, and the newest attempt seen per
  // row: late reports from either are dropped, so no row comes back.
  const closedTokensRef = useRef(new Set<number>());
  const [highWater] = useState(createLocalDownloadHighWater);
  const reportDownloadFailure = useCallback(
    (report: LocalDownloadFailureReport) => {
      if (isClosedLocalDownloadReport(closedTokensRef.current, report)) return;
      if (highWater.isStale(report.key, report.token)) return;
      highWater.note(report.key, report.token);
      setLocalFailures((current) =>
        addLocalDownloadFailure(current, report, Date.now())
      );
      dispatch({ type: 'expand' });
      trigger('error');
    },
    [highWater, trigger]
  );

  // Attempt tokens grow, so a newer attempt's row always wins.
  const attemptSeqRef = useRef(0);
  const nextLocalDownloadToken = useCallback(() => {
    attemptSeqRef.current += 1;
    return attemptSeqRef.current;
  }, []);
  const reportDownloadPreparing = useCallback(
    (report: { key: string; fileName: string }) => {
      const token = nextLocalDownloadToken();
      highWater.note(report.key, token);
      setLocalFailures((current) =>
        addLocalDownloadPreparing(current, { ...report, token }, Date.now())
      );
      dispatch({ type: 'expand' });
      return token;
    },
    [highWater, nextLocalDownloadToken]
  );
  const clearLocalDownload = useCallback(
    (key: string, token?: number | null) => {
      highWater.note(key, token);
      setLocalFailures((current) =>
        removeLocalDownloadFailure(current, localDownloadJobId(key), token)
      );
    },
    [highWater]
  );

  // App-lifetime runner for downloads prepared in this browser; it reaches
  // the latest row callbacks through a ref.
  const rowCallbacksRef = useRef({
    preparing: reportDownloadPreparing,
    fail: reportDownloadFailure,
    clear: clearLocalDownload,
  });
  useEffect(() => {
    rowCallbacksRef.current = {
      preparing: reportDownloadPreparing,
      fail: reportDownloadFailure,
      clear: clearLocalDownload,
    };
  }, [clearLocalDownload, reportDownloadFailure, reportDownloadPreparing]);
  const localRunnerRef = useRef<ReturnType<
    typeof createLocalDownloadRunner
  > | null>(null);
  const runLocalDownload = useCallback((job: LocalDownloadRun) => {
    localRunnerRef.current ??= createLocalDownloadRunner({
      preparing: (row) => rowCallbacksRef.current.preparing(row),
      fail: (report) => rowCallbacksRef.current.fail(report),
      clear: (key, token) => rowCallbacksRef.current.clear(key, token),
    });
    return localRunnerRef.current.run(job);
  }, []);
  useEffect(() => () => localRunnerRef.current?.dispose(), []);

  const contextValue = useMemo(
    () => ({
      handleFolderDownloadStart,
      registerAutoDelivery,
      reportDownloadFailure,
      runLocalDownload,
      nextLocalDownloadToken,
      clearLocalDownload,
    }),
    [
      clearLocalDownload,
      handleFolderDownloadStart,
      nextLocalDownloadToken,
      registerAutoDelivery,
      reportDownloadFailure,
      runLocalDownload,
    ]
  );
  const collapsedJob = useMemo(
    () => getDownloadManagerCollapsedJob(jobs),
    [jobs]
  );
  const canClose = canCloseDownloadManager(jobs, isDeliveryLocked);
  const shouldRenderManager = shouldRenderDownloadManager({
    hasResolvedDockCorner,
    isOpen: presentation.isOpen,
    jobCount: jobs.length,
    pathname,
    orgSlug: params.orgSlug,
  });

  const handleDownload = useCallback(
    async (job: DownloadManagerJob) => {
      if (isDeliveryLocked) return;
      setDownloadingJobId(job.id);
      try {
        await coordinator.deliverManually(job);
      } catch {
        // The manual action remains available for another attempt.
      } finally {
        setDownloadingJobId(null);
      }
    },
    [coordinator, isDeliveryLocked]
  );

  const handleRetry = useCallback(
    async (job: DownloadManagerJob) => {
      if (retryingJobId !== null) return;
      if (isLocalDownloadJob(job)) {
        const retry = localFailures.find(
          (failure) => failure.job.id === job.id
        )?.retry;
        if (!retry) return;
        // Once per row at a time; the row goes, and a second failure
        // reports exactly one fresh row (same key).
        await runLocalRetry(job.id, async () => {
          setLocalFailures((current) =>
            removeLocalDownloadFailure(current, job.id)
          );
          await retry();
        });
        return;
      }
      setRetryingJobId(job.id);
      try {
        await backend.retryDownload(job.id);
        trigger('success');
      } catch (error) {
        console.error('[download-manager] retry failed', error);
        trigger('error');
      } finally {
        setRetryingJobId(null);
      }
    },
    [backend, localFailures, retryingJobId, runLocalRetry, trigger]
  );

  const handleClose = useCallback(async () => {
    if (!canClose || isClosing) return;
    setIsClosing(true);
    try {
      await backend.suppressManagerJobs(backend.jobs.map((job) => job.id));
      for (const token of closedLocalDownloadTokens(localFailures))
        closedTokensRef.current.add(token);
      setLocalFailures([]);
      dispatch({ type: 'close' });
      trigger('tap');
    } catch {
      // Keep the terminal rows visible when suppression does not complete.
    } finally {
      setIsClosing(false);
    }
  }, [backend, canClose, isClosing, localFailures, trigger]);

  return (
    <DownloadManagerContext.Provider value={contextValue}>
      {children}
      {shouldRenderManager && collapsedJob ? (
        <DownloadManagerPanel
          jobs={jobs}
          copy={copy}
          locale={locale}
          isExpanded={presentation.isExpanded}
          isCloseDisabled={!canClose}
          isClosing={isClosing}
          isDeliveryLocked={isDeliveryLocked}
          downloadingJobId={downloadingJobId}
          retryingJobId={retryingJobId}
          collapsedJob={collapsedJob}
          downloadsHref={`/${params.orgSlug}/downloads`}
          dockCorner={presentation.dockCorner}
          onClose={() => void handleClose()}
          onDownload={(job) => void handleDownload(job)}
          onRetry={(job) => void handleRetry(job)}
          onExpand={() => {
            dispatch({ type: 'expand' });
            trigger('tap');
          }}
          onMinimize={() => {
            dispatch({ type: 'minimize' });
            trigger('tap');
          }}
          onDockCornerChange={handleDockCornerChange}
        />
      ) : null}
    </DownloadManagerContext.Provider>
  );
}

export function useDownloadManager(): DownloadManagerContextValue {
  const context = useOptionalDownloadManager();
  if (!context) {
    throw new Error(
      'useDownloadManager must be used within DownloadManagerProvider'
    );
  }
  return context;
}

export function useOptionalDownloadManager(): DownloadManagerContextValue | null {
  return useContext(DownloadManagerContext);
}

async function deliverJob(
  job: DownloadManagerJob,
  mintDownloadUrl: ReturnType<
    typeof useDownloadManagerBackend
  >['mintDownloadUrl']
): Promise<void> {
  const result = await mintDownloadUrl(job.id);
  await deliverManagedDownload(
    result.url,
    result.filename || job.archiveName,
    result.deliveryMode
  );
}
